/**
 * Per-frame WebGL shader pipeline for crt / chromatic / frostedGlass effects.
 *
 * Lifecycle:
 *  - One cache entry per source DOM element (frame root), keyed by WeakMap.
 *  - Each entry owns: a staging 2D canvas (offscreen, NOT layoutsubtree), a
 *    WebGL canvas (offscreen), the GL context + compiled programs + buffers.
 *  - We allocate lazily on first paint and reuse across paints.
 *  - GC: the WeakMap drops the entry automatically when the frame element is
 *    detached and collected — the offscreen canvases follow.
 *
 * Why staging is a separate canvas: the main canvas has `layoutsubtree`, and
 * `createImageBitmap(layoutsubtreeCanvas)` crashes. A vanilla offscreen 2D
 * canvas can `drawElementImage(el, 0, 0)` to sample the laid-out subtree, then
 * be uploaded to WebGL via `gl.texImage2D(..., stagingCanvas)` which works
 * fine. Staging may be DPR-scaled for crispness.
 */

import { crtVertexShader, crtFragmentShader } from './crt.glsl';
import { chromaticVertexShader, chromaticFragmentShader } from './chromatic.glsl';
import {
  frostedGlassVertexShader,
  frostedGlassBlurFragmentShader,
  frostedGlassTintFragmentShader,
} from './frostedGlass.glsl';

interface Ctx2DWithDrawElement extends CanvasRenderingContext2D {
  drawElementImage?: (el: Element, x: number, y: number) => DOMMatrix;
}

interface ProgramSet {
  program: WebGLProgram;
  attribs: { aPosition: number; aTexCoord: number };
  uniforms: Record<string, WebGLUniformLocation | null>;
}

interface FrameShaderCache {
  staging: HTMLCanvasElement;
  stagingCtx: Ctx2DWithDrawElement;
  glCanvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  quadBuffer: WebGLBuffer;
  texCoordBuffer: WebGLBuffer;
  inputTexture: WebGLTexture;
  pingTexture: WebGLTexture | null;
  pongTexture: WebGLTexture | null;
  pingFb: WebGLFramebuffer | null;
  pongFb: WebGLFramebuffer | null;
  programs: {
    crt?: ProgramSet;
    chromatic?: ProgramSet;
    blur?: ProgramSet;
    tint?: ProgramSet;
  };
  width: number;
  height: number;
  dpr: number;
  startTime: number;
}

const cache = new WeakMap<Element, FrameShaderCache>();

/**
 * One-time probe: do we have a working canvas 2D context + WebGL in this env?
 * jsdom returns null and logs a "not implemented" warning per call — without
 * this probe every paint would spam stderr in tests. We swallow the warning
 * via a temporary virtual-console handler scope only on the probe itself.
 */
let envSupportProbed = false;
let envSupports = false;
function envSupportsCanvas(): boolean {
  if (envSupportProbed) return envSupports;
  envSupportProbed = true;
  try {
    if (typeof document === 'undefined') return (envSupports = false);
    // jsdom doesn't implement canvas getContext — checking userAgent first avoids
    // a "not implemented" warning on every paint in unit tests.
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.userAgent === 'string' &&
      navigator.userAgent.toLowerCase().includes('jsdom')
    ) {
      return (envSupports = false);
    }
    const probeCanvas = document.createElement('canvas');
    probeCanvas.width = 1;
    probeCanvas.height = 1;
    const ctx = probeCanvas.getContext('2d');
    if (!ctx) return (envSupports = false);
    const gl =
      probeCanvas.getContext('webgl') ?? probeCanvas.getContext('experimental-webgl');
    envSupports = !!gl;
    return envSupports;
  } catch {
    return (envSupports = false);
  }
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error('shader alloc failed');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? '?';
    gl.deleteShader(sh);
    throw new Error(`shader compile: ${log}`);
  }
  return sh;
}

function linkProgram(
  gl: WebGLRenderingContext,
  vsSrc: string,
  fsSrc: string,
  uniformNames: string[],
): ProgramSet {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const program = gl.createProgram();
  if (!program) throw new Error('program alloc failed');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? '?';
    gl.deleteProgram(program);
    throw new Error(`program link: ${log}`);
  }
  const aPosition = gl.getAttribLocation(program, 'a_position');
  const aTexCoord = gl.getAttribLocation(program, 'a_texCoord');
  const uniforms: Record<string, WebGLUniformLocation | null> = {};
  for (const u of uniformNames) uniforms[u] = gl.getUniformLocation(program, u);
  return { program, attribs: { aPosition, aTexCoord }, uniforms };
}

function setupQuadBuffers(gl: WebGLRenderingContext): {
  quadBuffer: WebGLBuffer;
  texCoordBuffer: WebGLBuffer;
} {
  const quadBuffer = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW,
  );
  const texCoordBuffer = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
    gl.STATIC_DRAW,
  );
  return { quadBuffer, texCoordBuffer };
}

function createTexture(gl: WebGLRenderingContext, w: number, h: number): WebGLTexture {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return tex;
}

function createFramebuffer(gl: WebGLRenderingContext, tex: WebGLTexture): WebGLFramebuffer {
  const fb = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  return fb;
}

function bindAttribs(gl: WebGLRenderingContext, ps: ProgramSet, qBuf: WebGLBuffer, tBuf: WebGLBuffer) {
  gl.bindBuffer(gl.ARRAY_BUFFER, qBuf);
  gl.enableVertexAttribArray(ps.attribs.aPosition);
  gl.vertexAttribPointer(ps.attribs.aPosition, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, tBuf);
  gl.enableVertexAttribArray(ps.attribs.aTexCoord);
  gl.vertexAttribPointer(ps.attribs.aTexCoord, 2, gl.FLOAT, false, 0, 0);
}

/**
 * Get-or-allocate a cache entry for the given source element.
 * Returns null if WebGL isn't available (test envs without a real GL context).
 */
function getOrCreateCache(
  el: Element,
  w: number,
  h: number,
): FrameShaderCache | null {
  if (!envSupportsCanvas()) return null;
  let entry = cache.get(el);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  if (entry) {
    if (entry.width !== w || entry.height !== h || entry.dpr !== dpr) {
      resizeCache(entry, w, h, dpr);
    }
    return entry;
  }
  let staging: HTMLCanvasElement;
  let stagingCtx: Ctx2DWithDrawElement | null;
  let glCanvas: HTMLCanvasElement;
  let gl: WebGLRenderingContext | null;
  try {
    staging = document.createElement('canvas');
    staging.width = Math.max(1, Math.round(w * dpr));
    staging.height = Math.max(1, Math.round(h * dpr));
    stagingCtx = staging.getContext('2d') as Ctx2DWithDrawElement | null;
    if (!stagingCtx) return null;

    glCanvas = document.createElement('canvas');
    glCanvas.width = Math.max(1, Math.round(w * dpr));
    glCanvas.height = Math.max(1, Math.round(h * dpr));
    gl =
      (glCanvas.getContext('webgl', { premultipliedAlpha: true, antialias: false }) as
        | WebGLRenderingContext
        | null) ??
      (glCanvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
  } catch {
    // jsdom or other no-canvas envs throw from getContext. Treat as unavailable.
    return null;
  }
  if (!gl || !stagingCtx) return null;

  let quadBuffer: WebGLBuffer;
  let texCoordBuffer: WebGLBuffer;
  try {
    const bufs = setupQuadBuffers(gl);
    quadBuffer = bufs.quadBuffer;
    texCoordBuffer = bufs.texCoordBuffer;
  } catch {
    return null;
  }
  const inputTexture = createTexture(gl, staging.width, staging.height);

  entry = {
    staging,
    stagingCtx,
    glCanvas,
    gl,
    quadBuffer,
    texCoordBuffer,
    inputTexture,
    pingTexture: null,
    pongTexture: null,
    pingFb: null,
    pongFb: null,
    programs: {},
    width: w,
    height: h,
    dpr,
    startTime: typeof performance !== 'undefined' ? performance.now() : Date.now(),
  };
  cache.set(el, entry);
  return entry;
}

function resizeCache(entry: FrameShaderCache, w: number, h: number, dpr: number) {
  entry.width = w;
  entry.height = h;
  entry.dpr = dpr;
  const pw = Math.max(1, Math.round(w * dpr));
  const ph = Math.max(1, Math.round(h * dpr));
  entry.staging.width = pw;
  entry.staging.height = ph;
  entry.glCanvas.width = pw;
  entry.glCanvas.height = ph;
  const gl = entry.gl;
  gl.bindTexture(gl.TEXTURE_2D, entry.inputTexture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, pw, ph, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  if (entry.pingTexture) {
    gl.bindTexture(gl.TEXTURE_2D, entry.pingTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, pw, ph, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  }
  if (entry.pongTexture) {
    gl.bindTexture(gl.TEXTURE_2D, entry.pongTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, pw, ph, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  }
}

function ensurePingPong(entry: FrameShaderCache) {
  if (entry.pingTexture && entry.pongTexture && entry.pingFb && entry.pongFb) return;
  const gl = entry.gl;
  const pw = entry.staging.width;
  const ph = entry.staging.height;
  entry.pingTexture = createTexture(gl, pw, ph);
  entry.pongTexture = createTexture(gl, pw, ph);
  entry.pingFb = createFramebuffer(gl, entry.pingTexture);
  entry.pongFb = createFramebuffer(gl, entry.pongTexture);
}

/**
 * Sample the source element into the staging canvas. Returns true on success,
 * false on either drawElementImage absence or transient "no paint record"
 * failures. The caller treats false as "skip this frame".
 */
function sampleElementIntoStaging(entry: FrameShaderCache, el: Element): boolean {
  const ctx = entry.stagingCtx;
  if (typeof ctx.drawElementImage !== 'function') return false;
  try {
    ctx.reset();
    ctx.setTransform(entry.dpr, 0, 0, entry.dpr, 0, 0);
    ctx.drawElementImage(el, 0, 0);
    return true;
  } catch {
    return false;
  }
}

function uploadStagingToInputTexture(entry: FrameShaderCache) {
  const gl = entry.gl;
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.bindTexture(gl.TEXTURE_2D, entry.inputTexture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, entry.staging);
}

/** ---- CRT ---- */
export interface RunCrtParams {
  intensity: number;
}

export function runCrt(el: Element, w: number, h: number, params: RunCrtParams): HTMLCanvasElement | null {
  const entry = getOrCreateCache(el, w, h);
  if (!entry) return null;
  if (!sampleElementIntoStaging(entry, el)) return null;
  uploadStagingToInputTexture(entry);
  const gl = entry.gl;
  if (!entry.programs.crt) {
    entry.programs.crt = linkProgram(gl, crtVertexShader, crtFragmentShader, [
      'uTexture',
      'uResolution',
      'uIntensity',
      'uTime',
    ]);
  }
  const ps = entry.programs.crt;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, entry.glCanvas.width, entry.glCanvas.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(ps.program);
  bindAttribs(gl, ps, entry.quadBuffer, entry.texCoordBuffer);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, entry.inputTexture);
  gl.uniform1i(ps.uniforms.uTexture, 0);
  gl.uniform2f(ps.uniforms.uResolution, entry.glCanvas.width, entry.glCanvas.height);
  gl.uniform1f(ps.uniforms.uIntensity, clamp01(params.intensity));
  const t = (performance.now() - entry.startTime) / 1000;
  gl.uniform1f(ps.uniforms.uTime, t);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  return entry.glCanvas;
}

/** ---- Chromatic aberration ---- */
export interface RunChromaticParams {
  offset: number;
}

export function runChromatic(
  el: Element,
  w: number,
  h: number,
  params: RunChromaticParams,
): HTMLCanvasElement | null {
  const entry = getOrCreateCache(el, w, h);
  if (!entry) return null;
  if (!sampleElementIntoStaging(entry, el)) return null;
  uploadStagingToInputTexture(entry);
  const gl = entry.gl;
  if (!entry.programs.chromatic) {
    entry.programs.chromatic = linkProgram(gl, chromaticVertexShader, chromaticFragmentShader, [
      'uTexture',
      'uResolution',
      'uOffset',
    ]);
  }
  const ps = entry.programs.chromatic;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, entry.glCanvas.width, entry.glCanvas.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(ps.program);
  bindAttribs(gl, ps, entry.quadBuffer, entry.texCoordBuffer);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, entry.inputTexture);
  gl.uniform1i(ps.uniforms.uTexture, 0);
  gl.uniform2f(ps.uniforms.uResolution, entry.glCanvas.width, entry.glCanvas.height);
  gl.uniform1f(ps.uniforms.uOffset, params.offset * entry.dpr);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  return entry.glCanvas;
}

/** ---- Frosted glass ---- */
export interface RunFrostedGlassParams {
  blur: number;
  tint: [number, number, number, number];
}

export function runFrostedGlass(
  el: Element,
  w: number,
  h: number,
  params: RunFrostedGlassParams,
): HTMLCanvasElement | null {
  const entry = getOrCreateCache(el, w, h);
  if (!entry) return null;
  if (!sampleElementIntoStaging(entry, el)) return null;
  uploadStagingToInputTexture(entry);
  ensurePingPong(entry);
  const gl = entry.gl;
  if (!entry.programs.blur) {
    entry.programs.blur = linkProgram(gl, frostedGlassVertexShader, frostedGlassBlurFragmentShader, [
      'uTexture',
      'uResolution',
      'uDir',
      'uRadius',
    ]);
  }
  if (!entry.programs.tint) {
    entry.programs.tint = linkProgram(gl, frostedGlassVertexShader, frostedGlassTintFragmentShader, [
      'uTexture',
      'uTint',
    ]);
  }
  const blur = entry.programs.blur;
  const tint = entry.programs.tint;
  const pw = entry.glCanvas.width;
  const ph = entry.glCanvas.height;
  const radius = params.blur * entry.dpr;

  // Pass 1: horizontal blur (input → ping)
  gl.bindFramebuffer(gl.FRAMEBUFFER, entry.pingFb);
  gl.viewport(0, 0, pw, ph);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(blur.program);
  bindAttribs(gl, blur, entry.quadBuffer, entry.texCoordBuffer);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, entry.inputTexture);
  gl.uniform1i(blur.uniforms.uTexture, 0);
  gl.uniform2f(blur.uniforms.uResolution, pw, ph);
  gl.uniform2f(blur.uniforms.uDir, 1, 0);
  gl.uniform1f(blur.uniforms.uRadius, radius);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // Pass 2: vertical blur (ping → pong)
  gl.bindFramebuffer(gl.FRAMEBUFFER, entry.pongFb);
  gl.viewport(0, 0, pw, ph);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(blur.program);
  bindAttribs(gl, blur, entry.quadBuffer, entry.texCoordBuffer);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, entry.pingTexture);
  gl.uniform1i(blur.uniforms.uTexture, 0);
  gl.uniform2f(blur.uniforms.uResolution, pw, ph);
  gl.uniform2f(blur.uniforms.uDir, 0, 1);
  gl.uniform1f(blur.uniforms.uRadius, radius);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // Pass 3: tint (pong → default fb)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, pw, ph);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(tint.program);
  bindAttribs(gl, tint, entry.quadBuffer, entry.texCoordBuffer);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, entry.pongTexture);
  gl.uniform1i(tint.uniforms.uTexture, 0);
  gl.uniform4f(
    tint.uniforms.uTint,
    params.tint[0],
    params.tint[1],
    params.tint[2],
    params.tint[3],
  );
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  return entry.glCanvas;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/** Exposed for tests: drop a frame's cache (also fine to never call — WeakMap will GC). */
export function _dropCache(el: Element): void {
  cache.delete(el);
}
